import React, { useState } from "react";
import { Button, FormLabel, TreeNode, TreeView } from "@carbon/react";
import { Enterprise } from "@carbon/react/icons";
import C3NavigationSideBar from "../c3-navigation-sidebar/c3-navigation-sidebar";
import { SwitcherDivider } from "../c3-navigation-sidebar/components";
import { useOrgSidebarState } from "../c3-navigation-sidebar/c3-sidebar-state-provider";
import { useC3Profile } from "../../c3-user-configuration/c3-profile-provider/c3-profile-provider";
import { useC3UserConfiguration } from "../../c3-user-configuration/c3-user-configuration-provider";
import { ActiveOrgName, ActiveOrgNameWrapper, ActiveOrgWrapper, ActiveOrgWrapperCustom, ConfirmLeaveModal, ManageOrgOverflowMenu, OrgListWrapper, } from "./components";
import { leaveOrganization } from "../../../api/organizations";
function renderTree({ nodes, expanded, withIcons = false }) {
    if (!nodes) {
        return;
    }
    return nodes.map(({ children, renderIcon, isExpanded, isDisabled, ...nodeProps }) => (React.createElement(TreeNode, { key: nodeProps.id, renderIcon: withIcons ? renderIcon : null, isExpanded: expanded ?? isExpanded, ...(isDisabled && {
            style: { cursor: "not-allowed" },
            "aria-disabled": "true",
        }), ...nodeProps }, renderTree({ nodes: children, expanded, withIcons }))));
}
const C3OrgSidebar = ({ sideBar }) => {
    const { customElements, switchToOrg, manageOrg, manageSuperorg, manageTeam, ...sideBarProps } = sideBar;
    const { isOpen, setIsOpen, scrollBarWidth } = useOrgSidebarState();
    const { userToken, decodedAudience, domain, analyticsTrack, setActiveOrgId } = useC3UserConfiguration();
    const { activeOrg, orgs, removeOrg } = useC3Profile();
    const superOrgs = orgs?.filter((org) => org.isSuperOrg) || [];
    const hasSuperOrgs = superOrgs?.length;
    const isSuperOrgAdmin = activeOrg?.isSuperOrg
        ? activeOrg?.permissions?.org?.settings?.read
        : false;
    const teams = activeOrg?.childOrganizations?.filter(({ uuid }) => activeOrg?.isSuperOrg &&
        orgs?.some((org) => org.uuid === uuid) &&
        activeOrg?.uuid !== uuid);
    const isCustomized = Boolean(customElements?.activeOrganization);
    const [isConfirmLeaveModalOpen, setIsConfirmLeaveModalOpen] = useState(false);
    const [orgToLeave, setOrgToLeave] = useState(null);
    const [loadingStatus, setLoadingStatus] = useState("inactive");
    const isLastOrg = orgs?.length === 1;
    const isMemberOfTeam = (orgUuid) => orgs?.some(({ uuid }) => uuid === orgUuid) || false;
    const canUserLeaveOrg = (org) => (((isSuperOrgAdmin && isMemberOfTeam(org.uuid)) || !isSuperOrgAdmin) &&
        !org.permissions?.org?.users?.owner?.update &&
        orgs?.length &&
        orgs.length > 1) ||
        false;
    const canUserManageOrg = (org) => (isSuperOrgAdmin && !org.permissions) ||
        org.permissions?.org?.settings?.read ||
        false;
    const onManageCustomOrg = () => {
        if (sideBar.closeOnClick !== false)
            setIsOpen(false);
    };
    const canSwitchToOrg = (orgId) => orgs?.some((org) => org.uuid === orgId);
    const onManageOrg = ({ uuid, isSuperOrg }) => {
        setIsOpen(false);
        analyticsTrack?.("navBar:orgSettings:click", { orgId: activeOrg?.uuid });
        const isTeam = teams?.some((team) => team.uuid === uuid);
        if (isSuperOrg && manageSuperorg) {
            manageSuperorg(uuid);
        }
        else if (isSuperOrgAdmin && isTeam && manageTeam && activeOrg) {
            manageTeam(activeOrg?.uuid, uuid);
        }
        else if (manageOrg) {
            manageOrg(uuid);
        }
        else {
            window.location.href =
                isSuperOrgAdmin &&
                    !isSuperOrg &&
                    teams?.some((team) => team.uuid === uuid)
                    ? `https://console.${domain}/org/${activeOrg?.uuid}/team/${uuid}`
                    : `https://console.${domain}/org/${uuid}/${isSuperOrg ? "team" : "management"}`;
        }
    };
    const activeOrganization = customElements?.activeOrganization || {
        activeLabel: hasSuperOrgs ? "Organization" : "Active organization",
        orgName: activeOrg?.name || "",
        action: {
            onClick: onManageCustomOrg,
            label: "Manage",
        },
        otherLabel: "",
        teamsLabel: "Teams",
    };
    const switchOrg = (orgId, track) => {
        setActiveOrgId(orgId);
        if (track)
            analyticsTrack?.("navBar:orgSwitch:click", { orgId });
        switchToOrg?.(orgId);
        setIsOpen(false);
    };
    const confirmLeaveOrg = async () => {
        if (orgToLeave && decodedAudience && userToken) {
            setLoadingStatus("active");
            const { success } = await leaveOrganization({
                token: userToken,
                audience: decodedAudience,
                orgId: orgToLeave.uuid,
            });
            setLoadingStatus(success ? "finished" : "error");
            if (success) {
                setIsConfirmLeaveModalOpen(false);
                removeOrg(orgToLeave.uuid);
            }
            if (orgToLeave.uuid === activeOrg?.uuid) {
                const newActiveOrg = orgs?.[0]?.uuid;
                if (newActiveOrg)
                    switchOrg(newActiveOrg);
            }
        }
    };
    const itemTabIndex = isOpen ? undefined : -1;
    const groupedBySuperOrg = orgs?.reduce((acc, curr) => {
        const superOrgUuid = curr?.superOrganization?.uuid;
        if (!superOrgUuid) {
            acc[curr.uuid] = acc[curr.uuid] ?? {
                ...curr,
                childOrganizations: {},
            };
            return acc;
        }
        acc[superOrgUuid] = acc[superOrgUuid] ?? {
            ...curr?.superOrganization,
            childOrganizations: {},
        };
        acc[superOrgUuid].childOrganizations[curr.uuid] = {
            ...curr,
            childOrganizations: {},
        };
        return acc;
    }, {}) ?? {};
    return (React.createElement(C3NavigationSideBar, { sideBar: {
            ...sideBarProps,
            ariaLabel: sideBarProps.ariaLabel || "Organization Sidebars",
        }, icon: React.createElement(Enterprise, { size: 20 }) }, (customElements?.activeOrganization || activeOrg) && (React.createElement(React.Fragment, null,
        isCustomized ? (React.createElement(ActiveOrgWrapperCustom, null,
            React.createElement("div", { style: {
                    overflow: "hidden",
                    display: "grid",
                    gap: "4px",
                } },
                React.createElement(FormLabel, null, activeOrganization.activeLabel),
                React.createElement(ActiveOrgName, { className: "textPrimary", title: activeOrganization.orgName, "$isCustomized": true }, activeOrganization.orgName)),
            React.createElement(Button, { size: "md", kind: "ghost", onClick: () => {
                    activeOrganization.action.onClick();
                    onManageCustomOrg();
                }, tabIndex: itemTabIndex }, activeOrganization.action?.label))) : (React.createElement(ActiveOrgWrapper, { "$scrollBarWidth": scrollBarWidth },
            React.createElement(FormLabel, null, activeOrganization.activeLabel),
            React.createElement(ActiveOrgNameWrapper, null,
                React.createElement(ActiveOrgName, { className: "textPrimary", title: activeOrganization.orgName }, activeOrganization.orgName),
                activeOrg?.uuid && domain && (React.createElement(ManageOrgOverflowMenu, { canManage: canUserManageOrg(activeOrg), canLeave: canUserLeaveOrg(activeOrg), isLastOrg: isLastOrg, isOrgMember: isMemberOfTeam(activeOrg.uuid), orgName: activeOrg.name, onManage: () => onManageOrg(activeOrg), onRequestToLeave: () => {
                        setOrgToLeave(activeOrg);
                        setIsConfirmLeaveModalOpen(true);
                    } }))),
            React.createElement(ConfirmLeaveModal, { isOpen: isConfirmLeaveModalOpen, orgName: orgToLeave?.name || "", onConfirm: confirmLeaveOrg, onCancel: () => {
                    setIsConfirmLeaveModalOpen(false);
                    setOrgToLeave(null);
                }, loadingStatus: loadingStatus }))),
        React.createElement(SwitcherDivider, null),
        React.createElement(OrgListWrapper, null,
            React.createElement(TreeView, { label: "All organizations", ...(activeOrg?.uuid ? { selected: [activeOrg?.uuid] } : {}) }, renderTree({
                nodes: Object.values(groupedBySuperOrg)?.map((element) => {
                    const userTeams = Object.values(element?.childOrganizations)?.filter((child) => isMemberOfTeam(child.uuid));
                    const defaultIsExpanded = (activeOrg?.uuid === element.uuid ||
                        !isMemberOfTeam(element.uuid) ||
                        userTeams?.some((team) => activeOrg?.uuid === team.uuid)) ??
                        false;
                    return {
                        children: userTeams?.length > 0
                            ? userTeams.map((team) => {
                                return {
                                    id: team.uuid,
                                    label: team.name,
                                    isExpanded: false,
                                    value: team.uuid,
                                    renderIcon: null,
                                    onSelect: canSwitchToOrg(team.uuid)
                                        ? () => switchOrg(team.uuid, true)
                                        : undefined,
                                };
                            })
                            : null,
                        id: element.uuid,
                        label: element.name,
                        value: element.uuid,
                        renderIcon: null,
                        isExpanded: defaultIsExpanded,
                        isDisabled: !isMemberOfTeam(element.uuid),
                        onSelect: canSwitchToOrg(element.uuid)
                            ? () => switchOrg(element.uuid, true)
                            : undefined,
                    };
                }),
            })))))));
};
export default C3OrgSidebar;
